import { useEffect, useState } from 'react';
import * as ops from '../../domain/projectOps';
import { BPM_MAX, BPM_MIN, STEPS_PER_BAR, type MusicalKey } from '../../domain/schemas';
import { useObservable } from '../../lib/useObservable';
import { PITCH_CLASS_NAMES, SCALE_LABELS, SCALE_NAMES, type ScaleName } from '../../music/theory';
import { songDurationSeconds } from '../../music/compile';
import { useProject } from '../../state/projectContext';
import { useServices } from '../../state/services';
import { Alert, Button, Panel } from '../ui';

const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`;

export function KeySelect({ value, onChange, idPrefix }: { value: MusicalKey; onChange: (key: MusicalKey) => void; idPrefix: string }) {
  return (
    <div className="flex items-end gap-2">
      <div className="flex flex-col gap-1">
        <label htmlFor={`${idPrefix}-root`} className="text-xs font-medium text-zinc-300">
          Key
        </label>
        <select
          id={`${idPrefix}-root`}
          value={value.root}
          onChange={(e) => onChange({ ...value, root: Number(e.target.value) })}
          className="rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm"
        >
          {PITCH_CLASS_NAMES.map((n, i) => (
            <option key={n} value={i}>
              {n}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor={`${idPrefix}-scale`} className="sr-only">
          Scale
        </label>
        <select
          id={`${idPrefix}-scale`}
          value={value.scale}
          onChange={(e) => onChange({ ...value, scale: e.target.value as ScaleName })}
          className="rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm"
        >
          {SCALE_NAMES.map((s) => (
            <option key={s} value={s}>
              {SCALE_LABELS[s]}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

export function TransportBar() {
  const { project, update } = useProject();
  const { audio } = useServices();
  const engine = useObservable(audio.state);
  const [audioError, setAudioError] = useState<string | null>(null);
  const playing = engine.status === 'playing';
  const mode = project.transport.mode;
  const section = project.arrangement.sections.find((s) => s.id === engine.sectionId);
  const bar = engine.step >= 0 ? Math.floor(engine.step / STEPS_PER_BAR) + 1 : null;

  const toggle = async () => {
    setAudioError(null);
    try {
      await audio.toggle();
    } catch (error) {
      setAudioError(error instanceof Error ? error.message : 'Audio could not start.');
    }
  };

  // Space bar toggles playback when focus is not in a text field.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (event.code !== 'Space' || event.repeat || event.ctrlKey || event.metaKey) return;
      if (target?.closest('input, textarea, select, button, [role="gridcell"], [contenteditable="true"], a')) return;
      event.preventDefault();
      void audio.toggle().catch((e: unknown) => setAudioError(e instanceof Error ? e.message : 'Audio could not start.'));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [audio]);

  return (
    <Panel title="Transport">
      <div className="flex flex-wrap items-end gap-x-5 gap-y-3">
        <Button
          variant={playing ? 'secondary' : 'primary'}
          className="min-w-28"
          onClick={() => void toggle()}
          disabled={engine.status === 'starting'}
          aria-pressed={playing}
          data-testid="transport-toggle"
          title="Play / stop (Space)"
        >
          <span aria-hidden="true">{playing ? '■' : '▶'}</span> {playing ? 'Stop' : engine.status === 'starting' ? 'Starting…' : 'Start'}
        </Button>

        <fieldset className="flex flex-col gap-1">
          <legend className="text-xs font-medium text-zinc-300">Play</legend>
          <div className="flex overflow-hidden rounded-md border border-zinc-700">
            {(['pattern', 'song'] as const).map((m) => (
              <label key={m} className={`cursor-pointer px-3 py-1.5 text-sm has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-accent-400 ${mode === m ? 'bg-accent-600 text-white' : 'bg-zinc-900 text-zinc-300 hover:bg-zinc-800'}`}>
                <input
                  type="radio"
                  name="transport-mode"
                  value={m}
                  checked={mode === m}
                  onChange={() => update((p) => ops.setTransportMode(p, m))}
                  className="sr-only"
                  data-testid={`mode-${m}`}
                />
                {m === 'pattern' ? 'Pattern loop' : 'Song'}
              </label>
            ))}
          </div>
        </fieldset>
        {mode === 'song' && (
          <label className="flex items-center gap-2 pb-1.5 text-sm text-zinc-300">
            <input type="checkbox" checked={project.transport.loopSong} onChange={(e) => update((p) => ops.setLoopSong(p, e.target.checked))} />
            Loop song
          </label>
        )}

        <div className="flex min-w-52 flex-1 flex-col gap-1">
          <label htmlFor="bpm-range" className="text-xs font-medium text-zinc-300">
            Tempo (BPM)
          </label>
          <div className="flex items-center gap-2">
            <input
              id="bpm-range"
              type="range"
              min={BPM_MIN}
              max={BPM_MAX}
              value={project.settings.bpm}
              onChange={(e) => update((p) => ops.setBpm(p, Number(e.target.value)))}
              className="flex-1"
            />
            <input
              aria-label="Tempo in BPM"
              type="number"
              min={BPM_MIN}
              max={BPM_MAX}
              value={project.settings.bpm}
              data-testid="bpm-input"
              onChange={(e) => {
                const v = Number(e.target.value);
                if (Number.isFinite(v) && v >= BPM_MIN && v <= BPM_MAX) update((p) => ops.setBpm(p, v));
              }}
              className="w-18 rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm"
            />
          </div>
        </div>
        <div className="flex min-w-32 flex-col gap-1">
          <label htmlFor="swing-range" className="text-xs font-medium text-zinc-300">
            Swing {Math.round(project.settings.swing * 100)}%
          </label>
          <input
            id="swing-range"
            type="range"
            min={0}
            max={50}
            value={Math.round(project.settings.swing * 100)}
            onChange={(e) => update((p) => ops.setSwing(p, Number(e.target.value) / 100))}
          />
        </div>
        <KeySelect idPrefix="project-key" value={project.settings.key} onChange={(key) => update((p) => ops.setKey(p, key))} />
      </div>
      <p
        className="mt-3 text-xs text-zinc-400"
        role="status"
        data-testid="audio-status"
        data-status={engine.status}
        data-context={engine.contextState}
        data-step={engine.step}
        data-section={engine.sectionId ?? ''}
        data-parts={engine.scheduledParts}
      >
        {engine.contextState === 'none'
          ? 'Audio is off. Press Start to enable sound (browsers require a click or key press first).'
          : `Audio ${engine.status} · ${mode === 'song' ? `song ${fmt(songDurationSeconds(project))}` : 'pattern loop'}${
              bar ? ` · bar ${bar}${section ? ` · ${section.name}` : ''}` : ''
            }`}
      </p>
      {audioError && (
        <div className="mt-3">
          <Alert title="Audio could not start">{audioError}</Alert>
        </div>
      )}
    </Panel>
  );
}
